import type { Resource } from '@data-hub/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { LineChartIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { EmptyState as SharedEmptyState } from '@/components/empty-state';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/features/auth/auth-context';
import { useMe } from '@/features/auth/use-me';
import { buildCategoryLookup, useCategories } from '@/features/categories/use-categories';
import { DeleteResourceDialog } from '@/features/resources/DeleteResourceDialog';
import { EditResourceDialog } from '@/features/resources/EditResourceDialog';
import { ResourceCard } from '@/features/resources/ResourceCard';
import { useResources } from '@/features/resources/use-resources';
import { useSearch } from '@/features/search/search-context';

/**
 * Focused listing of dashboards-category resources. Reuses the canonical
 * `<ResourceCard>` so the visual identity matches `/resources` exactly,
 * but skips the chip filter row and the source/visibility popover — the
 * page is already filtered to a single category, so adding more controls
 * just adds visual noise. Sorting is by click count descending so the
 * dashboards people open most are at the top.
 *
 * Search comes from the global header search bar (`/dashboards` is added
 * to `SEARCH_ENABLED_PATHS` in AppHeader). Same pattern as `/resources`
 * and `/favorites`, so the user has consistent muscle memory.
 */
export const Route = createFileRoute('/_authed/dashboards')({
  component: DashboardsPage,
});

const CATEGORY_SLUG = 'dashboards';

function DashboardsPage() {
  const { user } = useAuth();
  const me = useMe();
  // Server-side filter by category — the API supports it natively, so we
  // never download non-dashboard resources to filter them away on the
  // client.
  const resources = useResources({ category: CATEGORY_SLUG });
  const categories = useCategories();
  const lookupCategory = buildCategoryLookup(categories.data?.items);
  const { search } = useSearch();

  const [editTarget, setEditTarget] = useState<Resource | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Resource | null>(null);

  const myEmail = user?.email ?? null;
  const isAdmin = me.data?.role === 'admin';
  // Depend on `resources.data` (stable from React Query) — the
  // `?? []` allocation produced a fresh array reference every render
  // and made this memo re-run uselessly.
  const filtered = useMemo(() => {
    const allItems = resources.data?.items ?? [];
    const list = search
      ? allItems.filter((r) => {
          const q = search.toLowerCase();
          return (
            r.title.toLowerCase().includes(q) ||
            r.url.toLowerCase().includes(q) ||
            r.tags.some((t) => t.toLowerCase().includes(q))
          );
        })
      : allItems;
    // Sort by activity: most-clicked first, ties break by createdAt desc so
    // the front of the grid is stable as clicks update.
    return [...list].sort((a, b) => {
      if (b.clicks !== a.clicks) return b.clicks - a.clicks;
      return b.createdAt.localeCompare(a.createdAt);
    });
  }, [resources.data, search]);
  const allItems = resources.data?.items ?? [];

  const isFiltering = !!search;

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6">
      <div className="flex items-end gap-3">
        <span className="grid size-10 place-items-center rounded-md bg-primary/10 text-primary">
          <LineChartIcon className="size-5" />
        </span>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboards</h1>
          <p className="text-sm text-muted-foreground">
            Live dashboards across the team — sorted by clicks.
          </p>
        </div>
      </div>

      {isFiltering && resources.data && (
        <p className="text-xs text-muted-foreground">
          Showing {filtered.length} of {allItems.length}
        </p>
      )}

      {resources.isPending ? (
        <DashboardsGridSkeleton />
      ) : resources.isError ? (
        <ErrorState message={resources.error.message} onRetry={() => resources.refetch()} />
      ) : filtered.length === 0 ? (
        <EmptyState isFiltering={isFiltering} />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((r) => {
            const cat = lookupCategory(r.category);
            const isFav = !!myEmail && r.favorites.includes(myEmail);
            const canManage = isAdmin || (myEmail !== null && r.createdBy === myEmail);
            return (
              <ResourceCard
                key={r.id}
                resource={r}
                category={cat}
                isFavorite={isFav}
                canManage={canManage}
                onEdit={() => setEditTarget(r)}
                onDelete={() => setDeleteTarget(r)}
              />
            );
          })}
        </div>
      )}

      <EditResourceDialog
        resource={editTarget}
        open={editTarget !== null}
        onOpenChange={(open) => !open && setEditTarget(null)}
      />
      <DeleteResourceDialog
        resource={deleteTarget}
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      />
    </div>
  );
}

function DashboardsGridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex flex-col gap-3 rounded-xl border bg-card p-6">
          <div className="flex items-start gap-3">
            <Skeleton className="size-10 rounded-md" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
          <Skeleton className="h-3 w-full" />
          <div className="flex gap-1.5">
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-5 w-12 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ isFiltering }: { isFiltering: boolean }) {
  if (isFiltering) {
    return (
      <SharedEmptyState
        emoji="🔎"
        title="No dashboards match your search"
        body="Try a shorter query or clear it to see everything."
      />
    );
  }
  return (
    <SharedEmptyState
      emoji="📈"
      title="No dashboards yet"
      body="Add a resource and pick the Dashboards category to surface it here."
      action={
        <Button asChild variant="default" size="sm">
          <Link to="/resources" search={{ create: '1' }}>
            Add a dashboard
          </Link>
        </Button>
      }
    />
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-8 text-center">
      <p className="text-sm text-destructive">{message}</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}
